import { Component, Input } from '@angular/core';
import { ColDef, GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { AgGridOptionsBuilder, AgGridFilterParams, AgGridColumns } from '../../../Compliance/AgGrid';
import { WeissmanTimestampMsecFormatPipe } from '../../../UI-Lib/Pipes/Date-Format/date-formatting.pipe';
import { TaxAccessSyncLogRepository } from '../taxAccessSyncLog.repository';
import { TaxAccessSyncLogDetailsAgGridDataSource } from './agGridDataSource';

@Component({
    selector: 'tax-access-sync-log-details-grid',
    template: `
        <ws-ag-grid-angular class="ag-theme-balham grid grid-cell-no-focus"
                            [gridOptions]="gridOptions"
                            [forceShowToolPanel]="true"
                            [showConfigureLayout]="false"
                            [defaultSizeToFit]="false"
                            [defaultSizeToContent]="true"
                            (gridReady)="onAgGridReady($event)">
        </ws-ag-grid-angular>
    `,
    standalone: false
})

export class TaxAccessSyncLogDetailsGridComponent {
    constructor(
        private readonly _datePipe: WeissmanTimestampMsecFormatPipe,
        private readonly _taxAccessSyncLogRepository: TaxAccessSyncLogRepository) {
    }

    @Input() summaryLogId: number;

    gridOptions: GridOptions = new AgGridOptionsBuilder({
        suppressScrollOnNewData: true
    })
        .withContext(this)
        .withLoadingOverlay()
        .withInfiniteScroll(1000)
        .withColumnResize()
        .withTextSelection()
        .build();

    private readonly _statusLabels: { [key: number]: string } = {
        [Core.TaxAccessSyncDetailStatusEnum.Sent]: 'Sent',
        [Core.TaxAccessSyncDetailStatusEnum.Skipped]: 'Skipped',
        [Core.TaxAccessSyncDetailStatusEnum.Failed]: 'Failed',
        [Core.TaxAccessSyncDetailStatusEnum.Unmapped]: 'Unmapped',
        [Core.TaxAccessSyncDetailStatusEnum.BatchPublished]: 'Batch Published',
        [Core.TaxAccessSyncDetailStatusEnum.BatchPublishFailed]: 'Batch Publish Failed',
        [Core.TaxAccessSyncDetailStatusEnum.BatchRecovery]: 'Batch Recovery',
        [Core.TaxAccessSyncDetailStatusEnum.BatchPublishing]: 'Batch Publishing'
    };

    private _gridApi: GridApi;

    onAgGridReady(event: GridReadyEvent): void {
        this._gridApi = event.api;

        const columns: ColDef[] = [
            {
                headerName: 'Detail ID',
                field: 'taxAccessSyncDetailLogId',
                width: AgGridColumns.numericColumnWidth
            },
            {
                headerName: 'Appeal ID',
                field: 'appealId',
                width: AgGridColumns.numericColumnWidth,
                filter: 'agNumberColumnFilter',
                filterParams: {
                    ...AgGridFilterParams.numberFilterParams,
                    filterOptions: ['equals', 'notEqual', 'blank', 'notBlank']
                },
                floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams
            },
            {
                headerName: 'Status',
                field: 'status',
                width: AgGridColumns.textColumnMedWidth,
                filter: 'agTextColumnFilter',
                filterParams: {
                    ...AgGridFilterParams.textFilterParams,
                    filterOptions: ['equals', 'notEqual']
                },
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                valueFormatter: (params) => {
                    const model = params.data as Core.TaxAccessSyncDetailLogModel;
                    if (!model) {
                        return '';
                    }

                    return this._statusLabels[model.status] || '';
                }
            },
            {
                headerName: 'Log Time',
                field: 'logTimeUtc',
                width: AgGridColumns.dateColumnWidth,
                sortable: false,
                valueFormatter: (params) => {
                    const model = params.data as Core.TaxAccessSyncDetailLogModel;
                    if (!(model && model.logTimeUtc)) {
                        return '';
                    }

                    return this._datePipe.transform(model.logTimeUtc);
                }
            },
            {
                headerName: 'Message',
                field: 'message',
                width: AgGridColumns.textColumnLargeWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                sortable: false
            }
        ];

        this._gridApi.setGridOption('columnDefs', columns);
        this._gridApi.sizeColumnsToFit();
        this._gridApi.updateGridOptions({
            datasource: new TaxAccessSyncLogDetailsAgGridDataSource(
                this._gridApi,
                this._taxAccessSyncLogRepository,
                this.summaryLogId)
        });
    }
}
